import { PDFFont } from 'pdf-lib';
import { PageGen, PdfBrush, PdfPoint } from '../pdf';
import { LatexNode, LatexBox, LayoutCtx, AtomType } from './types';
import { DRAWN_SYMBOLS, drawSymbol } from './symbols';

// Op functions that display subscripts/superscripts as LIMITS (below/above) in display style
// (e.g. \lim_{x→0} shows the limit below), as opposed to trig/log which keep scripts to the right.
const LIMITS_OP_FUNCTIONS = new Set([
  'lim',
  'limsup',
  'liminf',
  'max',
  'min',
  'sup',
  'inf',
  'det',
  'dim',
  'ker',
  'gcd',
  'lcm',
  'Pr',
  'hom',
  'arg',
  'deg',
]);

// ---------------------------------------------------------------------------
// Layout constants (em fractions)
// ---------------------------------------------------------------------------

const SCRIPT_RATIO = 0.7;
const AXIS_HEIGHT = 0.28;
const LINE_THICKNESS = 0.05;
const LINE_PADDING = 0.12;
const FRAC_SIDE_PAD = 0.1;
const SQRT_HOOK_W = 0.38;
const SQRT_GAP = 0.07;
const SUP_SHIFT = 0.45;
const SUB_SHIFT = 0.25;
const DELIM_PAD = 0.1;
const OP_SCALE = 1.4; // large op scale in display style
const COL_SEP = 0.55; // matrix column separation (em)
const ROW_SEP = 0.3; // matrix row separation (em)

// ---------------------------------------------------------------------------
// Math spacing table (TeX inter-atom spacing, in mu units, 18mu = 1em)
// 0=none, 3=thin, 4=med, 5=thick, -1=impossible/context
// ---------------------------------------------------------------------------

const SPACE_TABLE: Record<AtomType, Partial<Record<AtomType, number>>> = {
  ord: { op: 3, bin: 4, rel: 5, punct: 3, inner: 3 },
  op: { ord: 3, op: 3, rel: 5, punct: 3, inner: 3 },
  bin: { ord: 4, op: 4, open: 4, inner: 4 },
  rel: { ord: 5, op: 5, open: 5, inner: 5 },
  open: {},
  close: { op: 3, bin: 4, rel: 5, punct: 3, inner: 3 },
  punct: { ord: 3, op: 3, rel: 3, open: 3, close: 3, inner: 3, punct: 3 },
  inner: { ord: 3, op: 3, bin: 4, rel: 5, open: 3, inner: 3, punct: 3 },
};

function mathSpacing(left: AtomType, right: AtomType, size: number): number {
  const mu = size / 18;
  const muVal = SPACE_TABLE[left]?.[right] ?? 0;
  return muVal * mu;
}

// ---------------------------------------------------------------------------
// Low-level draw helpers
// ---------------------------------------------------------------------------

function drawText(
  page: PageGen,
  text: string,
  pdfFont: PDFFont,
  size: number,
  x: number,
  baselineY: number,
  brush: PdfBrush
): void {
  const xy = page.getXY(new PdfPoint(x, baselineY));
  page.page.drawText(text, {
    x: xy.x,
    y: xy.y,
    size,
    font: pdfFont,
    color: brush.color.toPdfLibColor(),
    opacity: brush.color.opacity,
  });
}

function drawPath(
  page: PageGen,
  pathStr: string,
  brush: PdfBrush,
  thickness: number,
  filled = false
): void {
  if (!pathStr) return;
  page.page.moveTo(0, page.page.getHeight());
  page.page.drawSvgPath(pathStr, {
    color: filled ? brush.color.toPdfLibColor() : undefined,
    borderColor: filled ? undefined : brush.color.toPdfLibColor(),
    borderWidth: filled ? 0 : thickness,
    opacity: brush.color.opacity,
  });
  page.page.moveTo(0, 0);
}

// ---------------------------------------------------------------------------
// Main layout function
// ---------------------------------------------------------------------------

export function layoutNode(node: LatexNode, ctx: LayoutCtx): LatexBox {
  switch (node.type) {
    case 'row':
      return layoutRow(node.items, ctx);
    case 'char':
      return layoutChar(node.ch, node.italic, node.atomType, ctx);
    case 'symbol':
      return layoutFontGlyph(node.ch, node.atomType, ctx);
    case 'drawn':
      return layoutDrawn(node.name, node.atomType, ctx);
    case 'text':
      return layoutTextRun(node.content, ctx);
    case 'space':
      return layoutSpace(node.em, ctx);
    case 'frac':
      return layoutFrac(node.num, node.den, ctx);
    case 'sqrt':
      return layoutSqrt(node.body, node.index, ctx);
    case 'script':
      return layoutScript(node.base, node.sup, node.sub, node.limits, ctx);
    case 'delimited':
      return layoutDelimited(node.open, node.close, node.body, ctx);
    case 'overline':
      return layoutOverline(node.body, ctx);
    case 'underline':
      return layoutUnderline(node.body, ctx);
    case 'op_func':
      return layoutOpFunc(node.name, ctx);
    case 'matrix':
      return layoutMatrix(node.rows, node.env, ctx);
    case 'accent':
      return layoutAccent(node.body, node.kind, ctx);
    case 'sized':
      return layoutNode(node.body, { ...ctx, size: ctx.size * node.sizeMul });
    case 'phantom': {
      const b = layoutNode(node.body, ctx);
      return { ...b, draw() {} };
    }
    case 'overbrace':
      return layoutOverbrace(node.body, node.label, ctx);
    case 'underbrace':
      return layoutUnderscore(node.body, node.label, ctx);
    case 'cancel':
      return layoutCancel(node.body, node.style, ctx);
    case 'stack':
      return layoutStack(node.body, node.over, node.under, ctx);
    case 'xarrow':
      return layoutXArrow(node.body, node.over, node.under, node.dir, ctx);
    case 'boxed':
      return layoutBoxed(node.body, ctx);
  }
}

// ---------------------------------------------------------------------------
// Row — with proper math spacing
// ---------------------------------------------------------------------------

function layoutRow(items: LatexNode[], ctx: LayoutCtx): LatexBox {
  if (items.length === 0) return emptyBox('ord');

  const boxes = items.map((item) => layoutNode(item, ctx));

  // Compute x offsets with inter-atom spacing
  const offsets: number[] = [0];
  let totalWidth = boxes[0].width;
  for (let i = 1; i < boxes.length; i++) {
    const sp = mathSpacing(boxes[i - 1].atomType, boxes[i].atomType, ctx.size);
    offsets.push(totalWidth + sp);
    totalWidth += sp + boxes[i].width;
  }

  const height = Math.max(0, ...boxes.map((b) => b.height));
  const depth = Math.max(0, ...boxes.map((b) => b.depth));

  return {
    width: totalWidth,
    height,
    depth,
    atomType: boxes[0].atomType,
    draw(page, x, baselineY, brush) {
      for (let i = 0; i < boxes.length; i++) {
        boxes[i].draw(page, x + offsets[i], baselineY, brush);
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Char / symbol / text
// ---------------------------------------------------------------------------

function layoutChar(ch: string, italic: boolean, atomType: AtomType, ctx: LayoutCtx): LatexBox {
  const font = italic ? ctx.italicFont : ctx.regularFont;
  return measureGlyph(ch, font, ctx.size, atomType);
}

function layoutFontGlyph(ch: string, atomType: AtomType, ctx: LayoutCtx): LatexBox {
  return measureGlyph(ch, ctx.regularFont, ctx.size, atomType);
}

function measureGlyph(ch: string, font: PDFFont, size: number, atomType: AtomType): LatexBox {
  const w = font.widthOfTextAtSize(ch, size);
  const ascent = font.heightAtSize(size, { descender: false });
  const fullH = font.heightAtSize(size, { descender: true });
  const depth = Math.max(0, fullH - ascent);
  const captFont = font;
  return {
    width: w,
    height: ascent,
    depth,
    atomType,
    draw(page, x, baselineY, brush) {
      drawText(page, ch, captFont, size, x, baselineY, brush);
    },
  };
}

function layoutTextRun(content: string, ctx: LayoutCtx): LatexBox {
  if (!content) return emptyBox('ord');
  const font = ctx.regularFont;
  const {size} = ctx;
  const w = font.widthOfTextAtSize(content, size);
  const ascent = font.heightAtSize(size, { descender: false });
  const fullH = font.heightAtSize(size, { descender: true });
  const depth = Math.max(0, fullH - ascent);
  return {
    width: w,
    height: ascent,
    depth,
    atomType: 'ord',
    draw(page, x, baselineY, brush) {
      drawText(page, content, font, size, x, baselineY, brush);
    },
  };
}

// ---------------------------------------------------------------------------
// Drawn symbol (SVG paths)
// ---------------------------------------------------------------------------

function layoutDrawn(name: string, atomType: AtomType, ctx: LayoutCtx): LatexBox {
  const fn = DRAWN_SYMBOLS[name];
  if (!fn) return emptyBox(atomType);

  // For large ops in display style: scale up
  const isLargeOp = atomType === 'op';
  const scale = isLargeOp && ctx.displayStyle && ctx.level === 0 ? OP_SCALE : 1;
  const s = ctx.size * scale;

  // Measure by calling with dummy position to get dimensions
  const shape = fn(0, 0, s);

  return {
    width: shape.w,
    height: shape.h,
    depth: shape.d,
    atomType,
    draw(page, x, baselineY, brush) {
      const sym = fn(x, baselineY, s);
      drawSymbol(sym, page, brush);
    },
  };
}

// ---------------------------------------------------------------------------
// Space
// ---------------------------------------------------------------------------

function layoutSpace(em: number, ctx: LayoutCtx): LatexBox {
  return {
    width: em * ctx.size,
    height: 0,
    depth: 0,
    atomType: 'ord',
    draw() {},
  };
}

// ---------------------------------------------------------------------------
// Fraction
// ---------------------------------------------------------------------------

function layoutFrac(numNode: LatexNode, denNode: LatexNode, ctx: LayoutCtx): LatexBox {
  // Fractions always use text-style for numerator/denominator.
  // In display style: full size but not display. In text style: shrink to script size.
  const fracSizeMul = ctx.displayStyle ? 1.0 : SCRIPT_RATIO;
  const subCtx = { ...ctx, displayStyle: false, size: ctx.size * fracSizeMul };
  const numBox = layoutNode(numNode, subCtx);
  const denBox = layoutNode(denNode, subCtx);

  const a = AXIS_HEIGHT * ctx.size;
  const t = LINE_THICKNESS * ctx.size;
  const p = LINE_PADDING * ctx.size;
  const sx = FRAC_SIDE_PAD * ctx.size;

  const fracW = Math.max(numBox.width, denBox.width) + 2 * sx;
  const height = a + t / 2 + p + numBox.depth + numBox.height;
  const depth = Math.max(0, -a + t / 2 + p + denBox.height + denBox.depth);

  return {
    width: fracW,
    height,
    depth,
    atomType: 'inner',
    draw(page, x, baselineY, brush) {
      const numBaselineY = baselineY - a - t / 2 - p - numBox.depth;
      const denBaselineY = baselineY - a + t / 2 + p + denBox.height;
      numBox.draw(page, x + sx + (fracW - 2 * sx - numBox.width) / 2, numBaselineY, brush);
      denBox.draw(page, x + sx + (fracW - 2 * sx - denBox.width) / 2, denBaselineY, brush);
      // fraction bar
      drawPath(page, `M ${x} ${baselineY - a} H ${x + fracW}`, brush, t);
    },
  };
}

// ---------------------------------------------------------------------------
// Sqrt
// ---------------------------------------------------------------------------

function layoutSqrt(
  bodyNode: LatexNode,
  indexNode: LatexNode | undefined,
  ctx: LayoutCtx
): LatexBox {
  const bodyBox = layoutNode(bodyNode, ctx);
  const hookW = SQRT_HOOK_W * ctx.size;
  const gap = SQRT_GAP * ctx.size;
  const t = LINE_THICKNESS * ctx.size;

  const width = hookW + bodyBox.width + t;
  const height = bodyBox.height + gap + t;
  const {depth} = bodyBox;

  const indexBox = indexNode ? layoutNode(indexNode, { ...ctx, size: ctx.size * 0.6 }) : null;

  return {
    width,
    height,
    depth,
    atomType: 'inner',
    draw(page, x, baselineY, brush) {
      const topY = baselineY - bodyBox.height - gap;
      const bottomY = baselineY + bodyBox.depth;
      const midY = topY + (bottomY - topY) * 0.55;

      const pathStr = [
        `M ${x + hookW * 0.05} ${midY}`,
        `L ${x + hookW * 0.35} ${bottomY}`,
        `L ${x + hookW} ${topY}`,
        `H ${x + hookW + bodyBox.width + t}`,
      ].join(' ');
      drawPath(page, pathStr, brush, t * 1.1);

      if (indexBox) {
        indexBox.draw(page, x, topY - gap * 0.3 + indexBox.depth, brush);
      }
      bodyBox.draw(page, x + hookW, baselineY, brush);
    },
  };
}

// ---------------------------------------------------------------------------
// Script (super/subscript), with limits support for large ops
// ---------------------------------------------------------------------------

function layoutScript(
  baseNode: LatexNode,
  supNode: LatexNode | undefined,
  subNode: LatexNode | undefined,
  wantsLimits: boolean | undefined,
  ctx: LayoutCtx
): LatexBox {
  const scriptCtx = {
    ...ctx,
    size: ctx.size * SCRIPT_RATIO,
    level: ctx.level + 1,
    displayStyle: false,
  };
  const baseBox = layoutNode(baseNode, ctx);
  const supBox = supNode ? layoutNode(supNode, scriptCtx) : null;
  const subBox = subNode ? layoutNode(subNode, scriptCtx) : null;

  // Use limits layout for:
  //  • Drawn large operators (\sum, \prod, \bigcup…) in display style
  //  • Limits-style text operators (\lim, \max, \min, \sup, \inf…) in display style
  // NOT for regular trig/log functions (\sin, \cos, \log…) or integrals (\int, \iint, \oint)
  // which always keep scripts to the right by default.
  const isIntegral =
    baseNode.type === 'drawn' &&
    (baseNode.name === 'int' || baseNode.name === 'iint' || baseNode.name === 'oint');
  const isDrawnLargeOp = baseNode.type === 'drawn' && baseBox.atomType === 'op' && !isIntegral;
  const isLimitsOpFunc = baseNode.type === 'op_func' && LIMITS_OP_FUNCTIONS.has(baseNode.name);
  const useLimits =
    wantsLimits || (ctx.displayStyle && ctx.level === 0 && (isDrawnLargeOp || isLimitsOpFunc));

  if (useLimits) {
    return layoutLimits(baseBox, supBox, subBox, ctx);
  }

  // For tall bases (e.g. parenthesized fractions), raise the superscript so it
  // sits near the top of the base rather than just 0.45em above the baseline.
  const supShift = Math.max(SUP_SHIFT * ctx.size, baseBox.height - 0.85 * ctx.size);
  const subShift = SUB_SHIFT * ctx.size;

  const scriptW = Math.max(supBox?.width ?? 0, subBox?.width ?? 0);
  const width = baseBox.width + scriptW;
  const height = Math.max(baseBox.height, supBox ? supShift + supBox.height : 0);
  const depth = Math.max(baseBox.depth, subBox ? subShift + subBox.depth : 0);

  return {
    width,
    height,
    depth,
    atomType: baseBox.atomType,
    draw(page, x, baselineY, brush) {
      baseBox.draw(page, x, baselineY, brush);
      if (supBox) supBox.draw(page, x + baseBox.width, baselineY - supShift, brush);
      if (subBox) subBox.draw(page, x + baseBox.width, baselineY + subShift, brush);
    },
  };
}

/** Limits layout: super above, sub below the base (centred) */
function layoutLimits(
  baseBox: LatexBox,
  supBox: LatexBox | null,
  subBox: LatexBox | null,
  ctx: LayoutCtx
): LatexBox {
  const gap = 0.15 * ctx.size;
  const width = Math.max(baseBox.width, supBox?.width ?? 0, subBox?.width ?? 0);

  const supH = supBox ? supBox.height + supBox.depth + gap : 0;
  const subH = subBox ? subBox.height + subBox.depth + gap : 0;

  const height = baseBox.height + supH;
  const depth = baseBox.depth + subH;

  return {
    width,
    height,
    depth,
    atomType: baseBox.atomType,
    draw(page, x, baselineY, brush) {
      const cx = (width - baseBox.width) / 2;
      baseBox.draw(page, x + cx, baselineY, brush);
      if (supBox) {
        const supX = x + (width - supBox.width) / 2;
        const supY = baselineY - baseBox.height - gap - supBox.depth;
        supBox.draw(page, supX, supY, brush);
      }
      if (subBox) {
        const subX = x + (width - subBox.width) / 2;
        const subY = baselineY + baseBox.depth + gap + subBox.height;
        subBox.draw(page, subX, subY, brush);
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Delimited (auto-scaling brackets)
// ---------------------------------------------------------------------------

function layoutDelimited(
  open: string,
  close: string,
  bodyNode: LatexNode,
  ctx: LayoutCtx
): LatexBox {
  const bodyBox = layoutNode(bodyNode, ctx);
  const totalH = bodyBox.height + bodyBox.depth;
  // Minimum width matches a real '(' character (~0.38em); also scale with height for tall bodies.
  const delimW = Math.max(ctx.size * 0.28, totalH * 0.2);
  const pad = DELIM_PAD * ctx.size;

  const width = delimW + pad + bodyBox.width + pad + delimW;
  const height = bodyBox.height + ctx.size * 0.06;
  const depth = bodyBox.depth + ctx.size * 0.06;

  return {
    width,
    height,
    depth,
    atomType: 'inner',
    draw(page, x, baselineY, brush) {
      bodyBox.draw(page, x + delimW + pad, baselineY, brush);
      drawAutoDelim(
        page,
        open,
        x,
        baselineY,
        delimW,
        bodyBox.height,
        bodyBox.depth,
        ctx.size,
        brush
      );
      drawAutoDelim(
        page,
        close,
        x + delimW + pad + bodyBox.width + pad,
        baselineY,
        delimW,
        bodyBox.height,
        bodyBox.depth,
        ctx.size,
        brush
      );
    },
  };
}

function drawAutoDelim(
  page: PageGen,
  delim: string,
  x: number,
  baselineY: number,
  w: number,
  bH: number,
  bD: number,
  size: number,
  brush: PdfBrush
): void {
  if (delim === '.' || !delim) return;
  const t = LINE_THICKNESS * size * 1.5;
  const top = baselineY - bH - size * 0.06;
  const bot = baselineY + bD + size * 0.06;
  const mid = (top + bot) / 2;
  const h = bot - top;

  let pathStr: string;

  // Special drawn delimiters
  const drawnOpen: Record<string, string> = { '⟨': 'langle', '⌊': 'lfloor', '⌈': 'lceil' };
  const drawnClose: Record<string, string> = { '⟩': 'rangle', '⌋': 'rfloor', '⌉': 'rceil' };
  const drawnKey = drawnOpen[delim] || drawnClose[delim];
  if (drawnKey) {
    const fn = DRAWN_SYMBOLS[drawnKey];
    if (fn) {
      // Scale to fit the totalH
      const totalH = bot - top;
      const symShape = fn(0, 0, size);
      const scaleF = totalH / (symShape.h + symShape.d);
      const scaledS = size * scaleF;
      const sym = fn(x, top + totalH * (symShape.h / (symShape.h + symShape.d)), scaledS);
      drawSymbol(sym, page, brush);
      return;
    }
  }

  if (delim === '(' || delim === ')') {
    // kX controls how "flat" the top/bottom are.
    // kY controls how "flat" the vertical middle is.
    const kX = 0.15;
    const kY = 0.4;

    if (delim === '(') {
      pathStr = [
        // Start at top-right
        `M ${x + w} ${top}`,
        // Upper curve:
        // Handle 1 stays near the top but moves left.
        // Handle 2 stays at the far left (x) but reaches down toward the middle.
        `C ${x + w - kX * w} ${top} ${x} ${mid - kY * h} ${x} ${mid}`,
        // Lower curve:
        // Handle 1 stays at far left (x) but reaches up toward the middle.
        // Handle 2 stays near the bottom but moves right.
        `C ${x} ${mid + kY * h} ${x + w - kX * w} ${bot} ${x + w} ${bot}`,
      ].join(' ');
    } else {
      // Mirror for ')'
      pathStr = [
        `M ${x} ${top}`,
        `C ${x + kX * w} ${top} ${x + w} ${mid - kY * h} ${x + w} ${mid}`,
        `C ${x + w} ${mid + kY * h} ${x + kX * w} ${bot} ${x} ${bot}`,
      ].join(' ');
    }
  } else if (delim === '[' || delim === ']') {
    if (delim === '[') pathStr = `M ${x + w} ${top} H ${x} V ${bot} H ${x + w}`;
    else pathStr = `M ${x} ${top} H ${x + w} V ${bot} H ${x}`;
  } else if (delim === '{' || delim === '}') {
    const serif = Math.min(w * 0.45, h * 0.08);
    const mid = (top + bot) / 2;
    const quarter = h * 0.25; // Used to find the "elbow" of the bracket

    if (delim === '{') {
      const outerX = x + w; // The tips and serifs (right side)
      const innerX = x; // The middle point (left side)
      const spineX = x + w * 0.5; // The vertical "back" of the bracket

      pathStr = [
        // 1. Start at top-right tip
        `M ${outerX} ${top}`,
        // 2. Top Serif
        `H ${outerX - serif}`,
        // 3. Curve from top-serif down to the spine "elbow"
        `C ${spineX} ${top} ${spineX} ${top + quarter} ${spineX} ${mid - quarter}`,
        // 4. Curve from elbow into the middle point (the "waist")
        `C ${spineX} ${mid} ${innerX} ${mid} ${innerX} ${mid}`,
        // 5. Curve back out from the point to the lower elbow
        `C ${innerX} ${mid} ${spineX} ${mid} ${spineX} ${mid + quarter}`,
        // 6. Curve from lower elbow to the bottom-serif
        `C ${spineX} ${bot - quarter} ${spineX} ${bot} ${outerX - serif} ${bot}`,
        // 7. Bottom Serif
        `H ${outerX}`,
      ].join(' ');
    } else {
      // Mirror logic for '}'
      const outerX = x; // The tips and serifs (left side)
      const innerX = x + w; // The middle point (right side)
      const spineX = x + w * 0.5;

      pathStr = [
        `M ${outerX} ${top}`,
        `H ${outerX + serif}`,
        `C ${spineX} ${top} ${spineX} ${top + quarter} ${spineX} ${mid - quarter}`,
        `C ${spineX} ${mid} ${innerX} ${mid} ${innerX} ${mid}`,
        `C ${innerX} ${mid} ${spineX} ${mid} ${spineX} ${mid + quarter}`,
        `C ${spineX} ${bot - quarter} ${spineX} ${bot} ${outerX + serif} ${bot}`,
        `H ${outerX}`,
      ].join(' ');
    }
  } else if (delim === '|') {
    pathStr = `M ${x + w / 2} ${top} V ${bot}`;
  } else if (delim === '‖') {
    pathStr = `M ${x + w * 0.3} ${top} V ${bot} M ${x + w * 0.7} ${top} V ${bot}`;
  } else {
    return;
  }
  drawPath(page, pathStr, brush, t);
}

// ---------------------------------------------------------------------------
// Overline / underline
// ---------------------------------------------------------------------------

function layoutOverline(bodyNode: LatexNode, ctx: LayoutCtx): LatexBox {
  const body = layoutNode(bodyNode, ctx);
  const gap = 0.06 * ctx.size;
  const t = LINE_THICKNESS * ctx.size;
  return {
    width: body.width,
    height: body.height + gap + t,
    depth: body.depth,
    atomType: body.atomType,
    draw(page, x, baselineY, brush) {
      body.draw(page, x, baselineY, brush);
      drawPath(page, `M ${x} ${baselineY - body.height - gap} H ${x + body.width}`, brush, t);
    },
  };
}

function layoutUnderline(bodyNode: LatexNode, ctx: LayoutCtx): LatexBox {
  const body = layoutNode(bodyNode, ctx);
  const gap = 0.04 * ctx.size;
  const t = LINE_THICKNESS * ctx.size;
  return {
    width: body.width,
    height: body.height,
    depth: body.depth + gap + t,
    atomType: body.atomType,
    draw(page, x, baselineY, brush) {
      body.draw(page, x, baselineY, brush);
      drawPath(page, `M ${x} ${baselineY + body.depth + gap} H ${x + body.width}`, brush, t);
    },
  };
}

// ---------------------------------------------------------------------------
// Operator functions (\sin, \cos, \log, …)
// ---------------------------------------------------------------------------

function layoutOpFunc(name: string, ctx: LayoutCtx): LatexBox {
  const box = layoutTextRun(name, ctx);
  return { ...box, atomType: 'op' };
}

// ---------------------------------------------------------------------------
// Matrix
// ---------------------------------------------------------------------------

function layoutMatrix(rows: LatexNode[][], env: string, ctx: LayoutCtx): LatexBox {
  if (rows.length === 0) return emptyBox('inner');

  // Layout all cells
  const cellBoxes: LatexBox[][] = rows.map((row) => row.map((cell) => layoutNode(cell, ctx)));

  const numCols = Math.max(...cellBoxes.map((r) => r.length));
  const numRows = cellBoxes.length;

  // Column widths
  const colWidths: number[] = Array.from({ length: numCols }, (_, c) =>
    Math.max(...cellBoxes.map((r) => r[c]?.width ?? 0))
  );
  // Row heights/depths
  const rowHeights: number[] = cellBoxes.map((r) => Math.max(...r.map((b) => b.height)));
  const rowDepths: number[] = cellBoxes.map((r) => Math.max(...r.map((b) => b.depth)));

  const colSep = COL_SEP * ctx.size;
  const rowSep = ROW_SEP * ctx.size;

  const totalW = colWidths.reduce((s, w) => s + w, 0) + colSep * (numCols - 1);
  const totalH =
    rowHeights.reduce((s, h) => s + h, 0) +
    rowDepths.reduce((s, d) => s + d, 0) +
    rowSep * (numRows - 1);

  // Center vertically on axis
  const axisY = AXIS_HEIGHT * ctx.size;
  const height = totalH / 2 + axisY;
  const depth = totalH / 2 - axisY;

  // Wrap in delimiters based on env
  const delimPairs: Record<string, [string, string]> = {
    pmatrix: ['(', ')'],
    bmatrix: ['[', ']'],
    vmatrix: ['|', '|'],
    Vmatrix: ['‖', '‖'],
    Bmatrix: ['{', '}'],
  };
  const [openD, closeD] = delimPairs[env] ?? ['', ''];

  const innerBox: LatexBox = {
    width: totalW,
    height,
    depth,
    atomType: 'inner',
    draw(page, x, baselineY, brush) {
      let rowY = baselineY - height; // top of matrix
      for (let r = 0; r < numRows; r++) {
        rowY += rowHeights[r]; // advance to baseline of this row
        let colX = x;
        for (let c = 0; c < numCols; c++) {
          const cell = cellBoxes[r][c];
          if (cell) {
            // Center cell horizontally within column
            const cx = colX + (colWidths[c] - cell.width) / 2;
            cell.draw(page, cx, rowY, brush);
          }
          colX += colWidths[c] + colSep;
        }
        rowY += rowDepths[r] + rowSep;
      }
    },
  };

  if (!openD) return innerBox;

  // Manually wrap with scaled delimiters
  const delimW = Math.max(ctx.size * 0.28, (height + depth) * 0.2);
  const pad = DELIM_PAD * ctx.size;
  const totalWidth = delimW + pad + totalW + pad + delimW;

  return {
    width: totalWidth,
    height: height + ctx.size * 0.06,
    depth: depth + ctx.size * 0.06,
    atomType: 'inner',
    draw(page, x, baselineY, brush) {
      drawAutoDelim(page, openD, x, baselineY, delimW, height, depth, ctx.size, brush);
      innerBox.draw(page, x + delimW + pad, baselineY, brush);
      drawAutoDelim(
        page,
        closeD,
        x + delimW + pad + totalW + pad,
        baselineY,
        delimW,
        height,
        depth,
        ctx.size,
        brush
      );
    },
  };
}

// ---------------------------------------------------------------------------
// Accents (\hat, \vec, \dot, \ddot, \tilde, \bar, \acute, \grave, \check, \breve)
// ---------------------------------------------------------------------------

function layoutAccent(bodyNode: LatexNode, kind: string, ctx: LayoutCtx): LatexBox {
  const body = layoutNode(bodyNode, ctx);
  const gap = 0.04 * ctx.size;
  const t = LINE_THICKNESS * ctx.size;
  const w = body.width;

  return {
    width: w,
    height: body.height + accentHeight(kind, ctx.size) + gap,
    depth: body.depth,
    atomType: body.atomType,
    draw(page, x, baselineY, brush) {
      body.draw(page, x, baselineY, brush);
      const topY = baselineY - body.height - gap;
      drawAccentMark(page, kind, x, topY, w, ctx.size, t, brush);
    },
  };
}

function accentHeight(kind: string, size: number): number {
  switch (kind) {
    case 'hat':
    case 'tilde':
      return 0.2 * size;
    case 'vec':
      return 0.22 * size;
    case 'dot':
      return 0.14 * size;
    case 'ddot':
      return 0.14 * size;
    case 'dddot':
      return 0.2 * size;
    case 'ddddot':
      return 0.26 * size;
    case 'bar':
      return 0.1 * size;
    case 'acute':
    case 'grave':
      return 0.2 * size;
    case 'check':
    case 'breve':
      return 0.2 * size;
    default:
      return 0.15 * size;
  }
}

function drawAccentMark(
  page: PageGen,
  kind: string,
  x: number,
  topY: number,
  w: number,
  size: number,
  t: number,
  brush: PdfBrush
): void {
  const cx = x + w / 2;
  const ah = accentHeight(kind, size);
  const mid = topY + ah;
  const hw = 0.1 * size; // arrowhead size

  switch (kind) {
    case 'hat':
      drawPath(page, `M ${x} ${mid} L ${cx} ${topY} L ${x + w} ${mid}`, brush, t);
      break;
    case 'tilde': {
      const amp = ah * 0.4;
      drawPath(
        page,
        `M ${x} ${mid} C ${x + w * 0.25} ${mid - amp} ${x + w * 0.75} ${mid + amp} ${x + w} ${mid}`,
        brush,
        t
      );
      break;
    }
    case 'vec': {
      // Right arrow above
      const aw = Math.min(w, 0.5 * size);
      drawPath(page, `M ${cx - aw / 2} ${mid} H ${cx + aw / 2 - hw}`, brush, t);
      drawPath(
        page,
        `M ${cx + aw / 2 - hw} ${mid - hw * 0.6} L ${cx + aw / 2} ${mid} L ${cx + aw / 2 - hw} ${mid + hw * 0.6} Z`,
        brush,
        t,
        true
      );
      break;
    }
    case 'dot': {
      const r = 0.05 * size;
      drawPath(
        page,
        `M ${cx - r} ${topY + r} A ${r} ${r} 0 1 0 ${cx + r} ${topY + r} A ${r} ${r} 0 1 0 ${cx - r} ${topY + r} Z`,
        brush,
        0,
        true
      );
      break;
    }
    case 'ddot': {
      const r = 0.05 * size;
      const gap2 = 0.15 * size;
      for (const dx of [-gap2, gap2]) {
        drawPath(
          page,
          `M ${cx + dx - r} ${topY + r} A ${r} ${r} 0 1 0 ${cx + dx + r} ${topY + r} A ${r} ${r} 0 1 0 ${cx + dx - r} ${topY + r} Z`,
          brush,
          0,
          true
        );
      }
      break;
    }
    case 'dddot': {
      const r = 0.04 * size;
      const gap2 = 0.12 * size;
      for (const dx of [-gap2, 0, gap2]) {
        drawPath(
          page,
          `M ${cx + dx - r} ${topY + r} A ${r} ${r} 0 1 0 ${cx + dx + r} ${topY + r} A ${r} ${r} 0 1 0 ${cx + dx - r} ${topY + r} Z`,
          brush,
          0,
          true
        );
      }
      break;
    }
    case 'ddddot': {
      const r = 0.035 * size;
      const gap2 = 0.1 * size;
      for (const dx of [-gap2 * 1.5, -gap2 * 0.5, gap2 * 0.5, gap2 * 1.5]) {
        drawPath(
          page,
          `M ${cx + dx - r} ${topY + r} A ${r} ${r} 0 1 0 ${cx + dx + r} ${topY + r} A ${r} ${r} 0 1 0 ${cx + dx - r} ${topY + r} Z`,
          brush,
          0,
          true
        );
      }
      break;
    }
    case 'bar':
      drawPath(page, `M ${x} ${mid} H ${x + w}`, brush, t);
      break;
    case 'acute':
      drawPath(page, `M ${cx - 0.05 * size} ${mid} L ${cx + 0.1 * size} ${topY}`, brush, t);
      break;
    case 'grave':
      drawPath(page, `M ${cx + 0.05 * size} ${mid} L ${cx - 0.1 * size} ${topY}`, brush, t);
      break;
    case 'check':
      drawPath(page, `M ${x} ${topY} L ${cx} ${mid} L ${x + w} ${topY}`, brush, t);
      break;
    case 'breve': {
      const r2 = (w / 2) * 0.9;
      drawPath(page, `M ${cx - r2} ${topY} A ${r2} ${r2} 0 0 0 ${cx + r2} ${topY}`, brush, t);
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// Overbrace / Underscore
// ---------------------------------------------------------------------------

function layoutOverbrace(
  bodyNode: LatexNode,
  labelNode: LatexNode | undefined,
  ctx: LayoutCtx
): LatexBox {
  const body = layoutNode(bodyNode, ctx);
  const braceH = 0.25 * ctx.size;
  const gap = 0.06 * ctx.size;
  const t = LINE_THICKNESS * ctx.size;

  let labelBox: LatexBox | undefined;
  let labelOffset = 0;
  if (labelNode) {
    labelBox = layoutNode(labelNode, { ...ctx, size: ctx.size * 0.7 });
    labelOffset = braceH + 0.05 * ctx.size;
  }

  const width = Math.max(body.width, labelBox ? labelBox.width : 0);
  const height = body.height + gap + braceH + (labelBox ? labelBox.height + labelOffset : 0);

  return {
    width,
    height,
    depth: body.depth,
    atomType: body.atomType,
    draw(page, x, baselineY, brush) {
      body.draw(page, x, baselineY, brush);
      const topY = baselineY - body.height - gap - braceH;
      const cx = x + width / 2;
      const braceW = Math.min(body.width * 0.9, width);
      const bx = x + (width - braceW) / 2;
      drawPath(
        page,
        `M ${bx} ${topY + braceH} Q ${bx} ${topY} ${bx + braceW * 0.15} ${topY} Q ${cx} ${topY + braceH * 0.3} ${bx + braceW * 0.85} ${topY} Q ${bx + braceW} ${topY} ${bx + braceW} ${topY + braceH}`,
        brush,
        t
      );
      if (labelBox) {
        labelBox.draw(
          page,
          x + (width - labelBox.width) / 2,
          topY - labelBox.height - 0.03 * ctx.size,
          brush
        );
      }
    },
  };
}

function layoutUnderscore(
  bodyNode: LatexNode,
  labelNode: LatexNode | undefined,
  ctx: LayoutCtx
): LatexBox {
  const body = layoutNode(bodyNode, ctx);
  const braceH = 0.25 * ctx.size;
  const gap = 0.06 * ctx.size;
  const t = LINE_THICKNESS * ctx.size;

  let labelBox: LatexBox | undefined;
  let labelOffset = 0;
  if (labelNode) {
    labelBox = layoutNode(labelNode, { ...ctx, size: ctx.size * 0.7 });
    labelOffset = braceH + 0.05 * ctx.size;
  }

  const width = Math.max(body.width, labelBox ? labelBox.width : 0);
  const {height} = body;
  const depth = body.depth + gap + braceH + (labelBox ? labelBox.height + labelOffset : 0);

  return {
    width,
    height,
    depth,
    atomType: body.atomType,
    draw(page, x, baselineY, brush) {
      body.draw(page, x, baselineY, brush);
      const botY = baselineY + body.depth + gap + braceH;
      const cx = x + width / 2;
      const braceW = Math.min(body.width * 0.9, width);
      const bx = x + (width - braceW) / 2;
      drawPath(
        page,
        `M ${bx} ${botY - braceH} Q ${bx} ${botY} ${bx + braceW * 0.15} ${botY} Q ${cx} ${botY - braceH * 0.3} ${bx + braceW * 0.85} ${botY} Q ${bx + braceW} ${botY} ${bx + braceW} ${botY - braceH}`,
        brush,
        t
      );
      if (labelBox) {
        labelBox.draw(page, x + (width - labelBox.width) / 2, botY + 0.03 * ctx.size, brush);
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Cancel / Strike-through
// ---------------------------------------------------------------------------

function layoutCancel(
  bodyNode: LatexNode,
  style: 'cancel' | 'bcancel' | 'xcancel',
  ctx: LayoutCtx
): LatexBox {
  const body = layoutNode(bodyNode, ctx);
  const t = LINE_THICKNESS * ctx.size;
  const pad = 0.05 * ctx.size;

  return {
    width: body.width + pad * 2,
    height: body.height + pad,
    depth: body.depth + pad,
    atomType: body.atomType,
    draw(page, x, baselineY, brush) {
      const bx = x + pad;
      body.draw(page, bx, baselineY, brush);
      const topY = baselineY - body.height - pad;
      const botY = baselineY + body.depth + pad;
      if (style === 'cancel' || style === 'xcancel') {
        drawPath(
          page,
          `M ${bx + body.width + pad - 0.05 * ctx.size} ${topY} L ${bx + 0.05 * ctx.size} ${botY}`,
          brush,
          t
        );
      }
      if (style === 'bcancel' || style === 'xcancel') {
        drawPath(
          page,
          `M ${bx + 0.05 * ctx.size} ${topY} L ${bx + body.width + pad - 0.05 * ctx.size} ${botY}`,
          brush,
          t
        );
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Stack (stackrel, underset, overset)
// ---------------------------------------------------------------------------

function layoutStack(
  bodyNode: LatexNode,
  overNode: LatexNode | undefined,
  underNode: LatexNode | undefined,
  ctx: LayoutCtx
): LatexBox {
  const body = layoutNode(bodyNode, ctx);
  const gap = 0.04 * ctx.size;

  let overBox: LatexBox | undefined;
  let underBox: LatexBox | undefined;

  if (overNode) {
    overBox = layoutNode(overNode, { ...ctx, size: ctx.size * 0.7 });
  }
  if (underNode) {
    underBox = layoutNode(underNode, { ...ctx, size: ctx.size * 0.7 });
  }

  const width = Math.max(body.width, overBox ? overBox.width : 0, underBox ? underBox.width : 0);
  const height = body.height + (overBox ? overBox.height + gap : 0);
  const depth = body.depth + (underBox ? underBox.height + gap : 0);

  return {
    width,
    height,
    depth,
    atomType: body.atomType,
    draw(page, x, baselineY, brush) {
      const cx = x + (width - body.width) / 2;
      const overY = overBox
        ? baselineY - body.height - gap - overBox.height
        : baselineY - body.height;
      const underY = baselineY + body.depth + gap;

      if (overBox) {
        const ox = x + (width - overBox.width) / 2;
        overBox.draw(page, ox, overY + overBox.height, brush);
      }
      body.draw(page, cx, baselineY, brush);
      if (underBox) {
        const ux = x + (width - underBox.width) / 2;
        underBox.draw(page, ux, underY, brush);
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Extensible arrows (xrightarrow, xleftarrow)
// ---------------------------------------------------------------------------

function layoutXArrow(
  bodyNode: LatexNode,
  overNode: LatexNode | undefined,
  underNode: LatexNode | undefined,
  dir: 'left' | 'right',
  ctx: LayoutCtx
): LatexBox {
  const body = layoutNode(bodyNode, ctx);
  const arrowW = Math.max(body.width + 0.3 * ctx.size, 0.6 * ctx.size);
  const arrowH = 0.08 * ctx.size;
  const gap = 0.06 * ctx.size;
  const t = LINE_THICKNESS * ctx.size;

  let overBox: LatexBox | undefined;
  let underBox: LatexBox | undefined;

  if (overNode) {
    overBox = layoutNode(overNode, { ...ctx, size: ctx.size * 0.7 });
  }
  if (underNode) {
    underBox = layoutNode(underNode, { ...ctx, size: ctx.size * 0.7 });
  }

  const labelW = Math.max(overBox ? overBox.width : 0, underBox ? underBox.width : 0, arrowW);
  const width = labelW;
  const height = arrowH * 2 + (overBox ? overBox.height + gap : 0) + 0.3 * ctx.size;
  const depth = underBox ? underBox.height + gap : 0;

  const isLeft = dir === 'left';

  return {
    width,
    height,
    depth,
    atomType: body.atomType,
    draw(page, x, baselineY, brush) {
      const cx = x + width / 2;
      const ay = baselineY - height + (overBox ? overBox.height + gap : 0) + arrowH;

      if (isLeft) {
        drawPath(
          page,
          `M ${x + arrowW} ${ay} H ${x + arrowH * 1.5} L ${x} ${ay + arrowH} L ${x + arrowH * 1.5} ${ay + arrowH * 2} H ${x + arrowW} V ${ay}`,
          brush,
          t
        );
      } else {
        drawPath(
          page,
          `M ${x} ${ay} H ${x + arrowW - arrowH * 1.5} L ${x + arrowW} ${ay + arrowH} L ${x + arrowW - arrowH * 1.5} ${ay + arrowH * 2} H ${x} V ${ay}`,
          brush,
          t
        );
      }

      if (overBox) {
        overBox.draw(page, x + (width - overBox.width) / 2, ay - gap - overBox.height, brush);
      }
      if (underBox) {
        underBox.draw(page, x + (width - underBox.width) / 2, ay + arrowH * 2 + gap, brush);
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Boxed
// ---------------------------------------------------------------------------

function layoutBoxed(bodyNode: LatexNode, ctx: LayoutCtx): LatexBox {
  const body = layoutNode(bodyNode, ctx);
  const pad = 0.08 * ctx.size;
  const t = LINE_THICKNESS * ctx.size;

  return {
    width: body.width + pad * 2,
    height: body.height + pad,
    depth: body.depth + pad,
    atomType: body.atomType,
    draw(page, x, baselineY, brush) {
      const bx = x + pad;
      body.draw(page, bx, baselineY, brush);
      const topY = baselineY - body.height - pad;
      const botY = baselineY + body.depth + pad;
      drawPath(
        page,
        `M ${x} ${topY} H ${x + body.width + pad * 2} V ${botY} H ${x} V ${topY}`,
        brush,
        t
      );
    },
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function emptyBox(atomType: AtomType): LatexBox {
  return { width: 0, height: 0, depth: 0, atomType, draw() {} };
}
